"use client";

import { Activity, Globe, TrendingUp } from "@/components/icons";
import type {
  AnalyticsCopy,
  AnalyticsView,
  PeriodDataset,
  PeriodKey,
  ProductVariant,
} from "./types";

export const ANALYTICS_VIEWS: AnalyticsView[] = [
  {
    key: "trends",
    icon: TrendingUp,
    title: "Usage Trends",
    description:
      "Bandwidth over time, down to the minute, with period-over-period change at a glance.",
  },
  {
    key: "domains",
    icon: Globe,
    title: "Top Domains",
    description:
      "Your most-requested destinations ranked by data volume, with the top 10 at a glance and the full list on demand.",
  },
  {
    key: "history",
    icon: Activity,
    title: "Connection History",
    description: "A live log of every connection: timestamp, target, access point, and size.",
  },
];

export const ANALYTICS_COPY: Record<ProductVariant, AnalyticsCopy> = {
  mobile: {
    eyebrow: "Live Analytics",
    heading: "Know where every gigabyte goes",
    body: "Every mobile access point ships with a real-time analytics dashboard. Track usage trends, see your top domains by volume, and audit full connection history, filtered by access point and time period.",
    accessPoints: ["All access points", "dallas-4g-01", "atlanta-5g-02"],
  },
  residential: {
    eyebrow: "Live Analytics",
    heading: "Full visibility into your residential traffic",
    body: "Every residential access point comes with a real-time analytics dashboard. Track usage trends, see your top domains by volume, and audit full connection history, filtered by access point and time period.",
    accessPoints: ["All access points", "us-pool-01", "eu-pool-02"],
  },
};

/** Deterministic scaling applied when a single access point is selected. */
export const AP_FACTORS = [1, 0.58, 0.42];

export const PERIOD_OPTIONS: Array<{ key: PeriodKey; label: string }> = [
  { key: "1h", label: "Last hour" },
  { key: "24h", label: "Last 24 hours" },
];

const HOURLY_GB = [
  3.1, 2.4, 1.9, 1.6, 1.8, 2.2, 3.4, 4.8, 6.1, 7.4, 8.2, 7.8, 8.6, 9.4, 8.8, 9.9, 11.2, 12.6, 13.4,
  12.1, 10.4, 8.2, 6.3, 4.6,
];

const MINUTE_GB = [0.42, 0.55, 0.48, 0.61, 0.72, 0.66, 0.81, 0.94, 0.88, 1.02, 1.12, 0.97];

export const PERIOD_DATA: Record<PeriodKey, PeriodDataset> = {
  "24h": {
    key: "24h",
    deltaLabel: "vs previous 24h",
    deltaPct: 18.2,
    labelEvery: 4,
    trends: HOURLY_GB.map((gb, i) => ({
      label: `${String(i).padStart(2, "0")}:00`,
      gb,
    })),
    domains: [
      { domain: "google.com", gb: 38.4 },
      { domain: "youtube.com", gb: 27.9 },
      { domain: "instagram.com", gb: 22.6 },
      { domain: "amazon.com", gb: 18.3 },
      { domain: "tiktok.com", gb: 15.1 },
      { domain: "linkedin.com", gb: 11.8 },
      { domain: "walmart.com", gb: 8.6 },
      { domain: "booking.com", gb: 6.9 },
      { domain: "ebay.com", gb: 5.2 },
      { domain: "zillow.com", gb: 3.8 },
      { domain: "reddit.com", gb: 3.1 },
      { domain: "netflix.com", gb: 2.7 },
      { domain: "x.com", gb: 2.4 },
      { domain: "twitch.tv", gb: 2.0 },
      { domain: "spotify.com", gb: 1.7 },
      { domain: "target.com", gb: 1.4 },
      { domain: "espn.com", gb: 1.1 },
      { domain: "airbnb.com", gb: 0.9 },
    ],
    history: [
      {
        id: "h24-1",
        time: "23:58:41",
        domain: "instagram.com",
        apIndex: 1,
        size: "4.2 MB",
        status: 200,
      },
      {
        id: "h24-2",
        time: "23:58:12",
        domain: "google.com",
        apIndex: 2,
        size: "1.8 MB",
        status: 200,
      },
      {
        id: "h24-3",
        time: "23:57:49",
        domain: "amazon.com",
        apIndex: 1,
        size: "3.6 MB",
        status: 200,
      },
      {
        id: "h24-4",
        time: "23:57:20",
        domain: "tiktok.com",
        apIndex: 2,
        size: "6.1 MB",
        status: 200,
      },
      {
        id: "h24-5",
        time: "23:56:55",
        domain: "youtube.com",
        apIndex: 1,
        size: "12.4 MB",
        status: 200,
      },
      {
        id: "h24-6",
        time: "23:56:31",
        domain: "booking.com",
        apIndex: 2,
        size: "920 KB",
        status: 200,
      },
      {
        id: "h24-7",
        time: "23:56:04",
        domain: "linkedin.com",
        apIndex: 1,
        size: "2.2 MB",
        status: 200,
      },
      {
        id: "h24-8",
        time: "23:55:38",
        domain: "walmart.com",
        apIndex: 2,
        size: "1.4 MB",
        status: 200,
      },
    ],
  },
  "1h": {
    key: "1h",
    deltaLabel: "vs previous hour",
    deltaPct: 6.4,
    labelEvery: 3,
    trends: MINUTE_GB.map((gb, i) => ({
      label: `-${60 - i * 5}m`,
      gb,
    })),
    domains: [
      { domain: "instagram.com", gb: 1.9 },
      { domain: "google.com", gb: 1.6 },
      { domain: "tiktok.com", gb: 1.28 },
      { domain: "youtube.com", gb: 1.1 },
      { domain: "amazon.com", gb: 0.84 },
      { domain: "booking.com", gb: 0.62 },
      { domain: "linkedin.com", gb: 0.51 },
      { domain: "walmart.com", gb: 0.34 },
      { domain: "zillow.com", gb: 0.22 },
      { domain: "ebay.com", gb: 0.18 },
      { domain: "reddit.com", gb: 0.15 },
      { domain: "x.com", gb: 0.13 },
      { domain: "netflix.com", gb: 0.11 },
      { domain: "twitch.tv", gb: 0.09 },
      { domain: "spotify.com", gb: 0.08 },
      { domain: "target.com", gb: 0.06 },
      { domain: "espn.com", gb: 0.05 },
      { domain: "airbnb.com", gb: 0.04 },
    ],
    history: [
      {
        id: "h1-1",
        time: "14:59:52",
        domain: "tiktok.com",
        apIndex: 2,
        size: "5.4 MB",
        status: 200,
      },
      {
        id: "h1-2",
        time: "14:59:30",
        domain: "instagram.com",
        apIndex: 1,
        size: "3.1 MB",
        status: 200,
      },
      {
        id: "h1-3",
        time: "14:59:07",
        domain: "google.com",
        apIndex: 1,
        size: "1.2 MB",
        status: 200,
      },
      {
        id: "h1-4",
        time: "14:58:44",
        domain: "youtube.com",
        apIndex: 2,
        size: "9.8 MB",
        status: 200,
      },
      {
        id: "h1-5",
        time: "14:58:19",
        domain: "amazon.com",
        apIndex: 1,
        size: "2.7 MB",
        status: 200,
      },
      {
        id: "h1-6",
        time: "14:57:56",
        domain: "linkedin.com",
        apIndex: 2,
        size: "1.6 MB",
        status: 200,
      },
      {
        id: "h1-7",
        time: "14:57:33",
        domain: "booking.com",
        apIndex: 1,
        size: "740 KB",
        status: 200,
      },
      {
        id: "h1-8",
        time: "14:57:10",
        domain: "zillow.com",
        apIndex: 2,
        size: "980 KB",
        status: 200,
      },
    ],
  },
};

export function formatGb(gb: number): string {
  if (gb >= 1) return `${gb.toFixed(1)} GB`;
  return `${Math.round(gb * 1024)} MB`;
}
